<script setup lang="ts">
import { computed, ref } from 'vue';
import { useMutation, useQuery } from '@tanstack/vue-query';
import { requirementsApi, type Requirement } from '@/api/planning/requirements';
import { weeklyPlansApi, type WeeklyPlan } from '@/api/planning/weekly-plans';
import { usePermissions } from '@/composables/usePermissions';
import { MONTH_NAMES_ES, totalExpectedVisitsInMonth } from '@/utils/monthly-expectation';
import WeeklyPlanForm from '@/components/weekly-plans/weekly-plan-form.vue';
import RequirementForm from './requirement-form.vue';
import RequirementCard from './requirement-card.vue';
import WeeklyScheduleDialog from './weekly-schedule-dialog.vue';

const ISO_DATE_LENGTH = 10;
const ISO_PAD_LENGTH = 2;

interface Props {
  patientId: number;
  patientGroupIds?: number[];
}

const props = defineProps<Props>();

const { hasPermission } = usePermissions();

const {
  data: requirements, refetch, isPending,
} = useQuery({
  queryKey: ['requirements', props.patientId],
  queryFn: () => requirementsApi.index(props.patientId),
  initialData: [] as Requirement[],
});

const showRequirementForm = ref(false);
const selectedRequirement = ref<Requirement | null>(null);
const showWeeklyPlanForm = ref(false);
const weeklyPlanContext = ref<{
  requirementId: number;
  career: string;
  skillId: number | null;
  skillName: string | null;
  blockedCaregiverIds: number[];
  durationMin: number;
} | null>(null);
const selectedWeeklyPlan = ref<WeeklyPlan | null>(null);
const showWeeklySchedule = ref(false);

const confirmDeleteRequirement = ref<Requirement | null>(null);
const confirmDeleteWeeklyPlan = ref<WeeklyPlan | null>(null);

function openCreateRequirement() {
  selectedRequirement.value = null;
  showRequirementForm.value = true;
}

function openEditRequirement(req: Requirement) {
  selectedRequirement.value = req;
  showRequirementForm.value = true;
}

function contextFor(req: Requirement) {
  return {
    requirementId: req.id, career: req.career, skillId: req.skillId, skillName: req.skillName,
    blockedCaregiverIds: req.blockedCaregiverIds ?? [], durationMin: req.durationMin,
  };
}

function openCreateWeeklyPlan(req: Requirement) {
  weeklyPlanContext.value = contextFor(req);
  selectedWeeklyPlan.value = null;
  showWeeklyPlanForm.value = true;
}

function openEditWeeklyPlan(req: Requirement, plan: WeeklyPlan) {
  weeklyPlanContext.value = contextFor(req);
  selectedWeeklyPlan.value = plan;
  showWeeklyPlanForm.value = true;
}

const { mutate: destroyRequirement } = useMutation({
  mutationFn: (id: number) => requirementsApi.destroy(id),
  onSuccess: () => {
    confirmDeleteRequirement.value = null;
    refetch();
  },
});

const { mutate: destroyWeeklyPlan } = useMutation({
  mutationFn: (id: number) => weeklyPlansApi.destroy(id),
  onSuccess: () => {
    confirmDeleteWeeklyPlan.value = null;
    refetch();
  },
});

const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth() + 1;
const currentMonthName = MONTH_NAMES_ES[now.getMonth()] ?? '';
const currentMonthNameTitle = currentMonthName.charAt(0).toUpperCase() + currentMonthName.slice(1);

const todayIso = [
  now.getFullYear(),
  String(now.getMonth() + 1).padStart(ISO_PAD_LENGTH, '0'),
  String(now.getDate()).padStart(ISO_PAD_LENGTH, '0'),
].join('-');

function isInactive(req: Requirement): boolean {
  return !!req.endsOn && req.endsOn.slice(0, ISO_DATE_LENGTH) < todayIso;
}

const activeRequirements = computed(
  () => requirements.value.filter((r) => !isInactive(r)),
);
const inactiveRequirements = computed(
  () => requirements.value.filter(isInactive),
);

const aggregateMonthlyVisits = computed(() => totalExpectedVisitsInMonth(
  activeRequirements.value, { year: currentYear, month: currentMonth },
));

const hasWeeklyRequirements = computed(
  () => activeRequirements.value.some((r) => r.frequency === 'weekly'),
);

const disciplineCount = computed(
  () => new Set(activeRequirements.value.map((r) => r.career)).size,
);

const canWrite = computed(() => hasPermission.value('requirements', 'create') ||
                                hasPermission.value('requirements', 'update'));
</script>

<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center justify-space-between flex-wrap ga-3 mb-4">
      <div>
        <div class="d-flex align-center ga-2">
          <v-icon
            icon="mdi-clipboard-list-outline"
            color="primary"
          />
          <span class="text-h6 font-weight-bold">Requerimientos</span>
        </div>
        <p class="text-body-2 text-medium-emphasis mt-1 mb-0">
          Turnos acordados por especialidad
        </p>
      </div>
      <div class="d-flex ga-2 flex-wrap">
        <v-btn
          v-if="hasWeeklyRequirements"
          color="primary"
          variant="outlined"
          prepend-icon="mdi-calendar-week"
          text="Vista semanal"
          @click="showWeeklySchedule = true"
        />
        <v-btn
          v-if="hasPermission('requirements', 'create')"
          color="primary"
          variant="flat"
          prepend-icon="mdi-plus"
          text="Nuevo requerimiento"
          @click="openCreateRequirement"
        />
      </div>
    </div>

    <!-- Summary cards -->
    <v-row
      v-if="activeRequirements.length > 0"
      dense
      class="mb-2"
    >
      <v-col
        cols="6"
        sm="4"
      >
        <v-card
          variant="tonal"
          color="primary"
        >
          <v-card-text class="py-3">
            <div class="text-caption text-medium-emphasis">
              Especialidades
            </div>
            <div class="text-h5 font-weight-bold">
              {{ disciplineCount }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="6"
        sm="4"
      >
        <v-card
          variant="tonal"
          color="info"
        >
          <v-card-text class="py-3">
            <div class="text-caption text-medium-emphasis">
              Requerimientos activos
            </div>
            <div class="text-h5 font-weight-bold">
              {{ activeRequirements.length }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col
        cols="12"
        sm="4"
      >
        <v-card
          variant="tonal"
          color="success"
        >
          <v-card-text class="py-3">
            <div class="text-caption text-medium-emphasis">
              Turnos Presupuestados en {{ currentMonthNameTitle }}
            </div>
            <div class="text-h5 font-weight-bold">
              {{ aggregateMonthlyVisits }}
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Loading skeleton -->
    <template v-if="isPending && requirements.length === 0">
      <v-skeleton-loader
        type="card"
        class="mb-3"
      />
      <v-skeleton-loader type="card" />
    </template>

    <!-- Empty state -->
    <v-card
      v-else-if="activeRequirements.length === 0"
      variant="outlined"
      class="pa-8 text-center"
    >
      <v-icon
        icon="mdi-calendar-plus"
        size="56"
        color="grey-darken-1"
      />
      <div class="text-subtitle-1 font-weight-medium mt-3">
        {{ inactiveRequirements.length === 0 ?
          'Este paciente aún no tiene requerimientos cargados' :
          'Sin requerimientos activos' }}
      </div>
      <p class="text-body-2 text-medium-emphasis mt-1 mb-4">
        {{ inactiveRequirements.length === 0 ?
          'Comienza definiendo los turnos por semana por cada especialidad.' :
          'Crea uno nuevo o consulta el historial finalizado abajo.' }}
      </p>
      <v-btn
        v-if="hasPermission('requirements', 'create')"
        color="primary"
        variant="flat"
        prepend-icon="mdi-plus"
        :text="inactiveRequirements.length === 0 ?
          'Crear primer requerimiento' : 'Nuevo requerimiento'"
        @click="openCreateRequirement"
      />
    </v-card>

    <!-- Active requirements -->
    <requirement-card
      v-for="req in activeRequirements"
      :key="req.id"
      :requirement="req"
      :current-year="currentYear"
      :current-month="currentMonth"
      :current-month-name="currentMonthName"
      @edit="openEditRequirement"
      @delete="confirmDeleteRequirement = $event"
      @add-plan="openCreateWeeklyPlan"
      @edit-plan="openEditWeeklyPlan"
      @delete-plan="confirmDeleteWeeklyPlan = $event"
    />

    <!-- Inactive requirements (collapsed) -->
    <v-expansion-panels
      v-if="inactiveRequirements.length > 0"
      class="mt-2 inactive-panel"
      variant="accordion"
    >
      <v-expansion-panel rounded="lg">
        <v-expansion-panel-title>
          <div class="d-flex align-center ga-2">
            <v-icon
              icon="mdi-archive-outline"
              size="small"
              color="grey-darken-1"
            />
            <span class="text-subtitle-2 font-weight-bold">
              Requerimientos finalizados
            </span>
            <v-chip
              size="x-small"
              variant="tonal"
              color="grey-darken-1"
            >
              {{ inactiveRequirements.length }}
            </v-chip>
          </div>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <requirement-card
            v-for="req in inactiveRequirements"
            :key="req.id"
            :requirement="req"
            :current-year="currentYear"
            :current-month="currentMonth"
            :current-month-name="currentMonthName"
            @edit="openEditRequirement"
            @delete="confirmDeleteRequirement = $event"
            @add-plan="openCreateWeeklyPlan"
            @edit-plan="openEditWeeklyPlan"
            @delete-plan="confirmDeleteWeeklyPlan = $event"
          />
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <!-- Forms -->
    <requirement-form
      v-if="canWrite"
      v-model="showRequirementForm"
      :patient-id="patientId"
      :requirement="selectedRequirement"
      @success="refetch"
    />

    <weekly-plan-form
      v-if="weeklyPlanContext"
      v-model="showWeeklyPlanForm"
      :requirement-id="weeklyPlanContext.requirementId"
      :patient-group-ids="patientGroupIds"
      :career="weeklyPlanContext.career"
      :skill-id="weeklyPlanContext.skillId"
      :skill-name="weeklyPlanContext.skillName"
      :blocked-caregiver-ids="weeklyPlanContext.blockedCaregiverIds"
      :duration-min="weeklyPlanContext.durationMin"
      :weekly-plan="selectedWeeklyPlan"
      @success="refetch"
    />

    <weekly-schedule-dialog
      v-model="showWeeklySchedule"
      :requirements="activeRequirements"
    />

    <!-- Confirm delete requirement -->
    <v-dialog
      :model-value="!!confirmDeleteRequirement"
      max-width="440"
      @update:model-value="(v: boolean) => !v && (confirmDeleteRequirement = null)"
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center ga-2 pa-4 pb-2">
          <v-icon
            icon="mdi-alert-outline"
            color="error"
          />
          <span class="text-h6 font-weight-bold">Eliminar requerimiento</span>
        </v-card-title>
        <v-card-text class="pa-4 pt-2">
          Esta acción eliminará también todos sus planes semanales.
          No afectará turnos ya creados. ¿Continuar?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            text="Cancelar"
            variant="text"
            @click="confirmDeleteRequirement = null"
          />
          <v-btn
            color="error"
            variant="flat"
            prepend-icon="mdi-delete"
            text="Eliminar"
            @click="destroyRequirement(confirmDeleteRequirement!.id)"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirm delete weekly plan -->
    <v-dialog
      :model-value="!!confirmDeleteWeeklyPlan"
      max-width="420"
      @update:model-value="(v: boolean) => !v && (confirmDeleteWeeklyPlan = null)"
    >
      <v-card rounded="lg">
        <v-card-title class="d-flex align-center ga-2 pa-4 pb-2">
          <v-icon
            icon="mdi-alert-outline"
            color="error"
          />
          <span class="text-h6 font-weight-bold">Eliminar plan semanal</span>
        </v-card-title>
        <v-card-text class="pa-4 pt-2">
          ¿Seguro que deseas eliminar este plan semanal?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer />
          <v-btn
            text="Cancelar"
            variant="text"
            @click="confirmDeleteWeeklyPlan = null"
          />
          <v-btn
            color="error"
            variant="flat"
            prepend-icon="mdi-delete"
            text="Eliminar"
            @click="destroyWeeklyPlan(confirmDeleteWeeklyPlan!.id)"
          />
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style scoped>
.inactive-panel :deep(.v-expansion-panel-text__wrapper) {
  padding-block: 8px;
  padding-inline: 0;
}
</style>
